<template>
  <!--
    New-layout REWARDS card. Renders the Figma AS-Monaco card: a full-bleed
    image at the top with an "Open" status pill + admin menu overlaid, a
    brand-red body panel holding the title / description, a white pill CTA, and
    a "Requirements" row of white gating-criterion pills.
  -->
  <div
    class="bg-fill-brand rounded-3xl shadow-[1px_3px_21px_0px_rgba(0,0,0,0.1)] w-[361px] overflow-hidden flex flex-col text-left"
  >
    <!-- Image region with overlaid status pill + admin menu -->
    <div class="relative h-[271px] w-full">
      <img
        v-if="offer.image_url"
        :src="offer.image_url"
        :alt="offer.name"
        class="absolute inset-0 size-full object-cover"
      />
      <div
        v-else
        class="absolute inset-0 flex items-center justify-center bg-fill-brand-secondary"
      >
        <Icon
          icon="mdi:tag-outline"
          class="text-icon-on-branded-surface text-5xl"
          aria-hidden="true"
        />
      </div>

      <!-- "Open" status pill (top-left) -->
      <div
        class="absolute left-2 top-2 flex items-center gap-2 rounded-full bg-surface px-3 py-1.5 h-8"
      >
        <span class="size-2 rounded-full bg-fill-brand" aria-hidden="true" />
        <span class="text-text text-sm font-medium leading-5">
          {{ statusLabel }}
        </span>
      </div>

      <!-- Admin menu (top-right), host-only -->
      <div v-if="canManageOffers" class="absolute right-2 top-2">
        <OfferItemDropdown :offer-id="offer.id" @deleted="emit('deleted')" />
      </div>
    </div>

    <!-- Brand-red body panel -->
    <div class="flex flex-col gap-4 px-4 pt-4 pb-4">
      <div class="flex flex-col gap-2">
        <p
          class="text-text-on-brand text-xl font-bold leading-[26px] line-clamp-2"
        >
          {{ offer.name }}
        </p>
        <p
          v-if="descriptionLabel"
          class="text-text-on-brand text-sm leading-[18px] line-clamp-2 opacity-90"
        >
          {{ descriptionLabel }}
        </p>
      </div>

      <!-- White pill CTA -->
      <div class="flex justify-center">
        <button
          type="button"
          class="rounded-full h-[50px] min-w-[124px] flex items-center justify-center px-8 gap-2 cursor-pointer transition-colors"
          :class="newCtaClasses"
          :aria-label="cardAriaLabel"
          @click="emit('cta-click')"
        >
          <Icon
            v-if="hasClaimed"
            icon="mdi:check-circle"
            class="size-4"
            aria-hidden="true"
          />
          <span class="text-base leading-5 font-medium text-center">
            {{ ctaLabel }}
          </span>
          <Icon
            v-if="isGated && !hasClaimed && !isSoldOut"
            icon="mdi:lock"
            class="size-4"
            aria-hidden="true"
          />
        </button>
      </div>

      <!-- Requirements row -->
      <div class="flex flex-col gap-2">
        <p class="text-text-on-brand text-sm font-semibold leading-5">
          {{ t("channel.offers.card.requirements") }}
        </p>
        <div class="flex flex-wrap items-center gap-1">
          <template v-if="criterionPills.length">
            <div
              v-for="pill in criterionPills"
              :key="pill.icon"
              class="flex items-center gap-2 rounded-full bg-surface h-8 px-3"
              :class="{ 'opacity-50': !pill.qualified }"
            >
              <img
                v-if="iconImageOverrides[pill.icon]"
                :src="iconImageOverrides[pill.icon]"
                alt=""
                class="size-4 object-contain"
              />
              <Icon
                v-else
                :icon="pill.icon"
                class="size-4 text-icon-emphasis"
                aria-hidden="true"
              />
              <span class="text-text text-sm font-medium leading-5">
                {{ t(pill.labelKey) }}
              </span>
            </div>
          </template>
          <div
            v-else
            class="flex items-center gap-2 rounded-full bg-surface h-8 px-3"
          >
            <Icon
              icon="mdi:earth"
              class="size-4 text-icon-emphasis"
              aria-hidden="true"
            />
            <span class="text-text text-sm font-medium leading-5">
              {{ t("channel.offers.card.openToEveryone") }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { Icon } from "@iconify/vue/dist/iconify.js";
import OfferItemDropdown from "./offer-item-dropdown.vue";
import type { OfferCardData } from "./offer-card.vue";
import type { OfferCriterionPill } from "./use-offer-card";

defineProps<{
  offer: OfferCardData;
  statusLabel: string;
  descriptionLabel: string;
  ctaLabel: string;
  newCtaClasses: string;
  cardAriaLabel: string;
  hasClaimed: boolean;
  isSoldOut: boolean;
  isGated: boolean;
  criterionPills: OfferCriterionPill[];
  iconImageOverrides: Record<string, string>;
  canManageOffers: boolean;
}>();

const emit = defineEmits<{
  "cta-click": [];
  deleted: [];
}>();

const { t } = useI18n();
</script>
